// extVersions.saga.js
import { put, all, takeLatest, takeEvery, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extractionsActionTypes from "../extractions/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";
import { parseBackendErrors } from "../../commonUtils/queryBuilder.helper.js";
import { setQBErrors } from "../extractions/action";
import { getOutputMappings } from "../extOutputMappings/actions";
import { sanitizeId } from "../../commonUtils/extraction.helper.js";
import { setCookie, deleteCookie } from '../../commonUtils/cookie.helper.js';
import { encrypt } from "../../commonUtils/crypto.helper";

// ============================================================================
// GET ALL VERSIONS
// ============================================================================
function* getExtVersionsSaga({ params }) {
    const { navigate = null, path = "" } = params;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const filters = yield select((state) => state.ExtVersions.filters);
        const extractionData = yield select((state) => state.Extractions.extractionData);

        let queryString = ``;
        if (filters.keyword) queryString = queryString.concat(`&keyword=${encodeURIComponent(filters.keyword)}`);
        if (filters.status)  queryString = queryString.concat(`&status=${filters.status}`);

        const { data } = yield API.get(`/ext/${extractionData.id}/versions?${queryString}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_VERSIONS_SUCCESS, payload: data.data });
            if (navigate && path) navigate(path);
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_EXT_VERSIONS_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        yield put({
            type: actionTypes.GET_EXT_VERSIONS_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ============================================================================
// GET SINGLE VERSION
// ============================================================================
function* getExtVersionSaga({ id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ type: actionTypes.GET_EXT_VERSION_FAILURE, payload: { message: "Extraction ID not found" } });
            return;
        }

        const { data } = yield API.get(`/ext/${extractionData.id}/versions/${id}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_VERSION_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load version.");
            yield put({ type: actionTypes.GET_EXT_VERSION_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not load version.");
        yield put({
            type: actionTypes.GET_EXT_VERSION_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ============================================================================
// GET VERSION BY KEY
// ============================================================================
function* getExtVersionFromKeySaga({ payload }) {
    try {
        const { key, navigate = null, pageName = null } = payload;
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.get(`/extractions/version/${key}`);
        if (data.success) {
            const responseData = data.data;
            if (responseData.version && responseData.extraction) {
                yield put({
                    type: actionTypes.GET_EXT_VERSION_BY_KEY_SUCCESS,
                    payload: { selected_columns: [], custom_columns: [], loading: false, ...responseData.version },
                });
                yield put({
                    type: extractionsActionTypes.SET_EXTRACTION_DATA,
                    payload: { ...responseData.extraction },
                });
                if (navigate && pageName !== 'outputMappings') {
                    yield put(getOutputMappings({ id: responseData.version._id, key: responseData.version.key, navigate }));
                }
            }
        } else {
            toast.error("Could not load version.");
            yield put({ type: actionTypes.GET_EXT_VERSION_BY_KEY_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not load version.");
        yield put({
            type: actionTypes.GET_EXT_VERSION_BY_KEY_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ============================================================================
// CREATE VERSION
// ============================================================================
function* createExtVersionSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_SINGLE_VERSION_LOADING, payload: true });

        const { formData, navigate, path } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ type: actionTypes.CREATE_EXT_VERSION_FAILURE, payload: { message: "Extraction ID not found" } });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/versions`, formData);

        if (data.success) {
            const createdVersion = data.data.extVersion;
            toast.success("Version created successfully!");

            yield put({ type: actionTypes.CREATE_EXT_VERSION_SUCCESS, payload: createdVersion });
            yield put({ type: extractionsActionTypes.SET_STEP_VAL, payload: 4 });
            
            // Clear the draft session cookie as the drafts are now successfully frozen!
            deleteCookie("_uds_");

            if (navigate && path) navigate(path);
            // NOTE: No START_POLLING dispatch here — the context monitoring effect
            // auto-starts the interval when it sees the new processing row in the list.
        } else {
            toast.error("Something went wrong");
            yield put({ type: actionTypes.CREATE_EXT_VERSION_FAILURE, payload: data });
        }
    } catch (error) {
        toast.error("Something went wrong");
        handleApiError(error, "Could not save version.");
        yield put({
            type: actionTypes.CREATE_EXT_VERSION_FAILURE,
            payload: {
                message: error?.response?.data?.message || "Internal server error",
                errors: error?.response?.data?.data?.errors || null,
            },
        });
    }
}

// ============================================================================
// RETRIGGER VERSION
// ============================================================================
function* retriggerExtVersionSaga({ payload }) {
    try {
        const { versionId } = payload;
        yield put({ type: actionTypes.SET_SINGLE_VERSION_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ type: actionTypes.RETRIGGER_EXT_VERSION_FAILURE, payload: { message: "Extraction ID not found" } });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/versions/${versionId}/retrigger`);

        if (data.success) {
            toast.success("Extraction process retriggered successfully!");
            yield put({ type: actionTypes.RETRIGGER_EXT_VERSION_SUCCESS, payload: data.data.extVersion });
            yield put({ type: actionTypes.GET_EXT_VERSIONS, params: {} });
            // NOTE: No START_POLLING here — GET_EXT_VERSIONS updates the list;
            // the context monitoring effect will start the timer automatically.
        } else {
            toast.error(data?.message || "Could not retrigger extraction.");
            yield put({ type: actionTypes.RETRIGGER_EXT_VERSION_FAILURE, payload: data });
        }
    } catch (error) {
        toast.error(error?.response?.data?.message || "Could not retrigger extraction.");
        handleApiError(error, "Could not retrigger extraction.");
        yield put({
            type: actionTypes.RETRIGGER_EXT_VERSION_FAILURE,
            payload: {
                message: error?.response?.data?.message || "Internal server error",
                errors: error?.response?.data?.data?.errors || null,
            },
        });
    }
}

// ============================================================================
// RETRY OUTPUT — manual single-version check (unchanged)
// ============================================================================
function* retryExtVersionOutputSaga({ id }) {
    try {
        yield put({ type: actionTypes.RETRY_EXT_VERSION_OUTPUT_REQUEST, id });

        const extractionData = yield select((state) => state.Extractions.extractionData);
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ type: actionTypes.RETRY_EXT_VERSION_OUTPUT_FAILURE, payload: { message: "Extraction ID not found" }, id });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/versions/${id}/retry-output`);

        if (data.success) {
            const status = data.data?.extVersion?.status || data.data?.status || data.data?.extVersion?.process_status || null;

            if (status === 'completed')      toast.success("Extraction completed successfully!");
            else if (status === 'failed')    toast.error("Extraction process has failed.");
            else if (status === 'timed_out') toast.error("Extraction process has timed out.");
            else                             toast.info("Extraction is still processing.");

            yield put({ type: actionTypes.RETRY_EXT_VERSION_OUTPUT_SUCCESS, payload: data.data, id });

            if (data.data?.extVersion) {
                yield put({ type: actionTypes.SET_EXT_VERSION, payload: sanitizeId(data.data.extVersion) });
            }
        } else {
            toast.error(data?.message || "Could not retry output check.");
            yield put({ type: actionTypes.RETRY_EXT_VERSION_OUTPUT_FAILURE, payload: data, id });
        }
    } catch (error) {
        handleApiError(error, "Could not retry output check.");
        yield put({
            type: actionTypes.RETRY_EXT_VERSION_OUTPUT_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
            id,
        });
    }
}

// ============================================================================
// BATCH STATUS CHECK — called by the context's setInterval (silent, no toasts)
// ============================================================================
function* batchStatusCheckSaga({ extId }) {
    try {
        if (!extId) return;

        const { data } = yield API.post(`/ext/${extId}/versions/batch-status-check`);

        if (data.success) {
            yield put({
                type: actionTypes.BATCH_STATUS_CHECK_SUCCESS,
                payload: data.data.versions, // flat array of updated version objects
            });
        }
        // Silent on failure — the timer will retry on the next tick
    } catch (error) {
        console.warn('[BatchPoll] Check failed, will retry next tick:', error.message);
    }
}

// ============================================================================
// UPDATE VERSION
// ============================================================================
function* updateExtVersionSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { id, formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ type: actionTypes.UPDATE_EXT_VERSION_FAILURE, payload: { message: "Extraction ID not found" } });
            return;
        }

        const { data } = yield API.put(`/ext/${extractionData.id}/versions/${id}`, formData);

        if (data.success) {
            toast.success("Version updated successfully!");
            yield put(setQBErrors({}));
            yield put({ type: actionTypes.UPDATE_EXT_VERSION_SUCCESS, payload: data.data });
            yield put({ type: actionTypes.GET_EXT_VERSIONS, params: {} });
        } else {
            toast.error(data?.message || "Could not save updated version data.");
            yield put({ type: actionTypes.UPDATE_EXT_VERSION_FAILURE, payload: data });
        }
    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data?.errors) {
            yield put(setQBErrors(parseBackendErrors(errorData.data.errors)));
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save updated version data.");
        }
        yield put({
            type: actionTypes.UPDATE_EXT_VERSION_FAILURE,
            payload: { message: errorData?.message || "Internal server error", errors: errorData?.data?.errors || null },
        });
    }
}

// ============================================================================
// UPDATE VERSIONS ORDER
// ============================================================================
function* updateExtVersionsOrderSaga({ payload }) {
    try {
        const { orderData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ type: actionTypes.UPDATE_EXT_VERSIONS_ORDER_FAILURE, payload: { message: "Extraction ID not found" } });
            return;
        }

        const { data } = yield API.put(`/ext/${extractionData.id}/versions/update-order`, { orderData });

        if (data.success) {
            yield put({ type: actionTypes.UPDATE_EXT_VERSIONS_ORDER_SUCCESS, payload: data.data });
        } else {
            toast.error(data?.message || "Could not update order.");
            yield put({ type: actionTypes.UPDATE_EXT_VERSIONS_ORDER_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not update order.");
        toast.error("Failed to update order. Refreshing...");
        yield put({ type: actionTypes.GET_EXT_VERSIONS, params: {} });
        yield put({
            type: actionTypes.UPDATE_EXT_VERSIONS_ORDER_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ============================================================================
// DELETE VERSION
// ============================================================================
function* deleteExtVersionSaga({ Id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);
        const { data } = yield API.delete(`/ext/${extractionData.id}/versions/${Id}`);

        if (data.success) {
            yield put({ type: actionTypes.DELETE_EXT_VERSION_SUCCESS, payload: data });
            yield put({ type: actionTypes.GET_EXT_VERSIONS, params: {} });
        } else {
            toast.error(data?.message || "Could not delete version.");
            yield put({ type: actionTypes.DELETE_EXT_VERSION_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not delete version.");
        toast.error(error?.response?.data?.message || "Could not delete version.");
        yield put({
            type: actionTypes.DELETE_EXT_VERSION_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ============================================================================
// MODAL SIDE-EFFECT
// ============================================================================
function* setOpenModalSaga({ payload }) {
    if (!payload) {
        yield put({ type: extractionsActionTypes.RESET_QUERY_BUILDER });
    }
}

// ============================================================================
// DOWNLOAD CSV
// ============================================================================
function* downloadExtVersionCsvSaga({ Key }) {
    try {
        yield put({ type: actionTypes.SET_DOWNLOADING, payload: true });

        const response = yield API.get(`/extractions/version/${Key}/download-csv`, { responseType: "blob" });

        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${Key}_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("CSV downloaded successfully!");
        yield put({ type: actionTypes.DOWNLOAD_EXT_VERSION_CSV_SUCCESS, payload: { Key } });
    } catch (error) {
        handleApiError(error, "Could not download CSV file.");
        const data = error?.response?.data;
        if (data instanceof Blob) {
            const text = yield data.text();
            try {
                toast.error(JSON.parse(text)?.message || "Could not download CSV file.");
            } catch {
                toast.error("Could not download CSV file.");
            }
        }
        yield put({
            type: actionTypes.DOWNLOAD_EXT_VERSION_CSV_FAILURE,
            payload: data || { message: "Internal server error" },
        });
    }
};

function* initiateDraftSessionSaga({ payload }) {
  const { extId, extKey, versionId, navigate } = payload;
  try {
    yield put({ type: actionTypes.SET_LOADING, payload: true });

    const { data } = yield API.post(`/ext/${extId}/versions/${versionId}/initiate-draft`);
    if (data.success) {
      yield put({ type: actionTypes.INITIATE_DRAFT_SESSION_SUCCESS, payload: data.data });
      const cookieData = { ext_id: extId, version_id: versionId };
      const encryptedExtData = encrypt(JSON.stringify(cookieData));
      setCookie('_uds_', encryptedExtData, '3650d'); 
      navigate(`/extraction/${extKey}`);
      yield put({ type: extractionsActionTypes.SET_STEP_VAL, payload: 2 });
    } else {
      toast.error(data.message || "Could not initiate draft session.");
      yield put({ type: actionTypes.INITIATE_DRAFT_SESSION_FAILURE, payload: data });
    }
  } catch (error) {
    handleApiError(error, "Failed to initiate draft session.");
    const errorData = error?.response?.data;
    yield put({
      type: actionTypes.INITIATE_DRAFT_SESSION_FAILURE,
      payload: errorData || { message: "Internal server error" },
    });
  } finally {
    yield put({ type: actionTypes.SET_LOADING, payload: false });
  }
};

// ============================================================================
// RECALCULATE VERSION
// ============================================================================
function* recalculateExtVersionSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_SINGLE_VERSION_LOADING, payload: true });

        const { extId, versionId, formData, navigate, path } = payload;
        
        const { data } = yield API.post(`/ext/${extId}/versions/${versionId}/recalculate`, formData);

        if (data.success) {
            toast.success("Recalculation processing initiated successfully!");
            yield put({ type: actionTypes.CREATE_EXT_VERSION_SUCCESS, payload: data.data.extVersion });
            yield put({ type: extractionsActionTypes.SET_STEP_VAL, payload: 4 });

            // Clear the draft session cookie as the drafts are now successfully frozen!
            deleteCookie("_uds_");

            if (navigate && path) navigate(path);
        } else {
            toast.error(data?.message || "Something went wrong during recalculation.");
            yield put({ type: actionTypes.CREATE_EXT_VERSION_FAILURE, payload: data });
        }
    } catch (error) {
        toast.error(error?.response?.data?.message || "Could not recalculate version.");
        handleApiError(error, "Could not recalculate version.");
        yield put({
            type: actionTypes.CREATE_EXT_VERSION_FAILURE,
            payload: {
                message: error?.response?.data?.message || "Internal server error",
                errors: error?.response?.data?.data?.errors || null,
            },
        });
    } finally {
        yield put({ type: actionTypes.SET_SINGLE_VERSION_LOADING, payload: false });
    }
}

// ============================================================================
// ROOT SAGA
// ============================================================================
function* ExtVersionsSaga() {
    yield all([
        takeLatest(actionTypes.GET_EXT_VERSIONS,            getExtVersionsSaga),
        takeLatest(actionTypes.GET_EXT_VERSION,             getExtVersionSaga),
        takeLatest(actionTypes.GET_EXT_VERSION_BY_KEY,      getExtVersionFromKeySaga),
        takeLatest(actionTypes.CREATE_EXT_VERSION,          createExtVersionSaga),
        takeLatest(actionTypes.RETRIGGER_EXT_VERSION,       retriggerExtVersionSaga),
        takeLatest(actionTypes.UPDATE_EXT_VERSION,          updateExtVersionSaga),
        takeLatest(actionTypes.UPDATE_EXT_VERSIONS_ORDER,   updateExtVersionsOrderSaga),
        takeLatest(actionTypes.DELETE_EXT_VERSION,          deleteExtVersionSaga),
        takeLatest(actionTypes.SET_OPEN_MODAL,              setOpenModalSaga),
        takeLatest(actionTypes.DOWNLOAD_EXT_VERSION_CSV,    downloadExtVersionCsvSaga),
        takeLatest(actionTypes.BATCH_STATUS_CHECK,          batchStatusCheckSaga),
        takeEvery(actionTypes.RETRY_EXT_VERSION_OUTPUT,     retryExtVersionOutputSaga),
        takeLatest(actionTypes.INITIATE_DRAFT_SESSION,      initiateDraftSessionSaga),
        takeLatest(actionTypes.RECALCULATE_EXT_VERSION,     recalculateExtVersionSaga),
    ]);
}

export default ExtVersionsSaga;